import * as React from 'react'
import { EquipmentStack } from './EquipmentStack'
import { Badge, Button, Dialog, MoreMenu } from './ui'
import { addDays, dateLabel, money, type OpsExtension } from './operationsModel'
import './bookingDetails.css'

export type BookingAction = { label: string; onClick: () => void; isDisabled?: boolean; variant?: 'destructive' }

export function BookingDetails({ reference, status, payment, equipment, actions = [], busy = false, onClose, children }: {
  reference: string; status?: string; payment?: string | null; actions?: BookingAction[]; busy?: boolean
  equipment?: { productType?: string | null; quantity: number }
  onClose: () => void; children: React.ReactNode
}) {
  const titleId = React.useId()
  return <Dialog isOpen onOpenChange={open => { if (!open && !busy) onClose() }} purpose={busy ? 'required' : 'info'} width="min(850px, 96vw)" maxHeight="92dvh" aria-labelledby={titleId}>
    <div className="booking-popup">
      <header className="booking-popup-header">
        <div className="booking-popup-identity">{equipment && <EquipmentStack productType={equipment.productType} quantity={equipment.quantity} />}<div className="booking-popup-title"><span className="booking-eyebrow">Booking details</span><h2 id={titleId}>{reference}</h2><div className="booking-statuses">{status && <Badge label={status.replaceAll('_', ' ')} />}{payment && <Badge label={payment.replaceAll('_', ' ')} />}</div></div></div>
        <div className="booking-popup-controls">{actions.length > 0 && <MoreMenu label="Booking actions" alignment="end" isDisabled={busy} items={actions} />}<Button type="button" aria-label="Close booking details" disabled={busy} onClick={onClose}>×</Button></div>
      </header>
      <div className="booking-popup-body">{children}</div>
    </div>
  </Dialog>
}

export function BookingSection({ title, children }: { title: string; children: React.ReactNode }) {
  return <section className="booking-section" aria-label={title}><h3>{title}</h3>{children}</section>
}
export function BookingField({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="booking-field"><dt>{label}</dt><dd>{children || '—'}</dd></div>
}
export function BookingCustomer({ name, phone, email, address }: { name?: string | null; phone?: string | null; email?: string | null; address?: string | null }) {
  return <BookingSection title="Customer details"><dl className="booking-fields">
    <BookingField label="Name">{name || 'Awaiting customer'}</BookingField>
    <BookingField label="Phone">{phone ? <a href={`tel:${phone}`}>{phone}</a> : 'Not recorded'}</BookingField>
    <BookingField label="Address"><span className="booking-preserve-lines">{address || 'Not recorded'}</span></BookingField>
    <BookingField label="Email">{email ? <a href={`mailto:${email}`}>{email}</a> : 'Not recorded'}</BookingField>
  </dl></BookingSection>
}
export function BookingDates({ start, end, collection, online = false }: { start: string; end: string; collection: string; online?: boolean }) {
  return <dl className="booking-dates">
    <BookingField label={online ? 'Delivery' : 'Start date'}>{dateLabel(start)}</BookingField>
    <BookingField label="Hire end">{dateLabel(end)}</BookingField>
    <BookingField label={online ? 'Collection' : 'Return date'}>{dateLabel(collection)}</BookingField>
  </dl>
}
export function BookingExtensions({ extensions, renderInvoice }: { extensions: OpsExtension[]; renderInvoice?: (extension: OpsExtension) => React.ReactNode }) {
  const sorted = [...extensions].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id))
  return <BookingSection title="Extensions">{sorted.length ? <>
    <ol className="booking-extensions">{sorted.map((extension, index) => <li key={extension.id}>
      <div className="booking-extension-heading"><div><span className="booking-eyebrow">Extension {index + 1}</span><strong>{dateLabel(addDays(extension.previous_end_date, 1))} → {dateLabel(extension.new_end_date)}</strong></div><Badge label={extension.payment_status} /></div>
      <div className="booking-extension-meta"><strong>{money(extension.value_pence)}</strong>{extension.invoice_url && /^https?:\/\//i.test(extension.invoice_url) && <a href={extension.invoice_url} target="_blank" rel="noreferrer">Open invoice ↗</a>}</div>
      {renderInvoice?.(extension)}
      {extension.notes && <p className="booking-preserve-lines">{extension.notes}</p>}
    </li>)}</ol>
    <p className="booking-muted">Extensions total: {money(sorted.reduce((total, item) => total + Number(item.value_pence || 0), 0))}</p>
  </> : <p className="booking-muted">No extensions recorded.</p>}</BookingSection>
}
